import {
  fetchTransactions,
  fetchTransaction,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getTransactionStages,
  updateTransactionStage,
  getTransactionStatistics,
  reviewTransaction,
  getPropertyTransactions,
  getCustomerTransactions
} from '@/services/transactionService'
import logger from '@/utils/logger'

const state = {
  transactions: [],
  currentTransaction: null,
  loading: false,
  transactionStages: [],
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 20
  }
}

const mutations = {
  SET_TRANSACTIONS(state, transactions) {
    state.transactions = transactions
  },

  SET_CURRENT_TRANSACTION(state, transaction) {
    state.currentTransaction = transaction
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = pagination
  },

  ADD_TRANSACTION(state, transaction) {
    state.transactions.unshift(transaction)
  },

  UPDATE_TRANSACTION(state, updatedTransaction) {
    const index = state.transactions.findIndex(t => t.id === updatedTransaction.id)
    if (index !== -1) {
      state.transactions.splice(index, 1, updatedTransaction)
    }
  },

  REMOVE_TRANSACTION(state, transactionId) {
    state.transactions = state.transactions.filter(t => t.id !== transactionId)
  },

  SET_TRANSACTION_STAGES(state, stages) {
    state.transactionStages = stages
  },

  ADD_TRANSACTION_STAGE(state, stage) {
    state.transactionStages.push(stage)
  },

  UPDATE_TRANSACTION_STAGE(state, updatedStage) {
    const index = state.transactionStages.findIndex(s => s.id === updatedStage.id)
    if (index !== -1) {
      state.transactionStages.splice(index, 1, updatedStage)
    }
  },

  REMOVE_TRANSACTION_STAGE(state, stageId) {
    state.transactionStages = state.transactionStages.filter(s => s.id !== stageId)
  }
}

const actions = {
  async fetchTransactions({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const result = await fetchTransactions(params)
      commit('SET_TRANSACTIONS', result.transactions || [])
      commit('SET_PAGINATION', result.pagination || {})
      return result
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTransaction({ commit }, transactionId) {
    commit('SET_LOADING', true)
    try {
      const transaction = await fetchTransaction(transactionId)
      commit('SET_CURRENT_TRANSACTION', transaction)
      return transaction
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createTransaction({ commit }, transactionData) {
    try {
      const result = await createTransaction(transactionData)
      const newTransaction = result.transaction || { ...transactionData, id: Date.now() }
      commit('ADD_TRANSACTION', newTransaction)
      return newTransaction
    } catch (error) {
      logger.error('Failed to create transaction:', error)
      throw error
    }
  },

  async updateTransaction({ commit }, { id, data }) {
    try {
      const result = await updateTransaction(id, data)
      const updatedTransaction = result.transaction || { id, ...data }
      commit('UPDATE_TRANSACTION', updatedTransaction)
      commit('SET_CURRENT_TRANSACTION', updatedTransaction)
      return updatedTransaction
    } catch (error) {
      logger.error(`Failed to update transaction ${id}:`, error)
      throw error
    }
  },

  async deleteTransaction({ commit }, transactionId) {
    try {
      await deleteTransaction(transactionId)
      commit('REMOVE_TRANSACTION', transactionId)
    } catch (error) {
      logger.error(`Failed to delete transaction ${transactionId}:`, error)
      throw error
    }
  },

  async fetchTransactionStages({ commit }) {
    try {
      const stages = await getTransactionStages()
      commit('SET_TRANSACTION_STAGES', stages)
      return stages
    } catch (error) {
      logger.error('Failed to fetch transaction stages:', error)
      throw error
    }
  },

  async updateTransactionStage({ commit }, { transactionId, stageData }) {
    try {
      const result = await updateTransactionStage(transactionId, stageData)
      const updatedTransaction = result.transaction
      if (updatedTransaction) {
        commit('UPDATE_TRANSACTION', updatedTransaction)
        commit('SET_CURRENT_TRANSACTION', updatedTransaction)
        return updatedTransaction
      }
      return null
    } catch (error) {
      logger.error(`Failed to update transaction ${transactionId} stage:`, error)
      throw error
    }
  },

  async fetchTransactionStatistics(_, filters = {}) {
    try {
      return await getTransactionStatistics(filters)
    } catch (error) {
      logger.error('Failed to fetch transaction statistics:', error)
      throw error
    }
  },

  async reviewTransaction({ commit }, { transactionId, action, comment }) {
    try {
      const result = await reviewTransaction(transactionId, action, comment)
      const reviewedTransaction = result.transaction
      if (reviewedTransaction) {
        commit('UPDATE_TRANSACTION', reviewedTransaction)
        commit('SET_CURRENT_TRANSACTION', reviewedTransaction)
        return reviewedTransaction
      }
      return null
    } catch (error) {
      logger.error(`Failed to review transaction ${transactionId}:`, error)
      throw error
    }
  },

  async fetchPropertyTransactions(_, { propertyId, options = {} }) {
    try {
      return await getPropertyTransactions(propertyId, options)
    } catch (error) {
      logger.error(`Failed to fetch transactions for property ${propertyId}:`, error)
      return []
    }
  },

  async fetchCustomerTransactions(_, { customerId, options = {} }) {
    try {
      return await getCustomerTransactions(customerId, options)
    } catch (error) {
      logger.error(`Failed to fetch transactions for customer ${customerId}:`, error)
      return []
    }
  }
}

const getters = {
  transactions: state => state.transactions,
  currentTransaction: state => state.currentTransaction,
  loading: state => state.loading,
  pagination: state => state.pagination,
  transactionStages: state => state.transactionStages,
  enabledTransactionStages: state => state.transactionStages.filter(stage => stage.is_active),

  // Filter transactions by type
  paymentTransactions: state => state.transactions.filter(t => t.transaction_type === '收款'),
  collectionTransactions: state => state.transactions.filter(t => t.transaction_type === '代收代付'),

  // Filter transactions by review status
  pendingReviewTransactions: state => state.transactions.filter(t => t.review_status === 'pending'),
  approvedTransactions: state => state.transactions.filter(t => t.review_status === 'approved'),
  rejectedTransactions: state => state.transactions.filter(t => t.review_status === 'rejected'),

  // Get transactions by property ID
  getTransactionsByProperty: state => propertyId => {
    return state.transactions.filter(t => t.property_id === propertyId)
  },

  // Get transactions by customer ID
  getTransactionsByCustomer: state => customerId => {
    return state.transactions.filter(t => t.customer_id === customerId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}